import { useQueryClient } from '@tanstack/react-query';
import { useState, type ClipboardEvent, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import { Gomb, Mezo, Parbeszed, Valaszto } from '../ui/elemek.tsx';
import { hibajegyBekuldese, type BekuldesEredmeny } from './api.ts';
import { HibajegyHiba } from './Cimkek.tsx';
import { kepekEllenorzese } from './kepek.ts';
import KepValaszto from './KepValaszto.tsx';
import { CIM_MAX, LEIRAS_MAX, oldalUrl, PRIORITAS_CIMKE, PRIORITAS_SORREND, urlapHiba, type HibajegyPrioritas } from './tipusok.ts';

/** Lebegő „Hiba jelzése” gomb minden belső oldalon, és az űrlap párbeszédablaka. Mobilon az alsó navigáció fölött, kis körként ül. */
export default function HibaGomb() {
  const { irodak, aktivId, jogok } = useAktivIroda();
  const hely = useLocation();
  const qc = useQueryClient();
  const [nyitva, setNyitva] = useState(false);
  const [cim, setCim] = useState('');
  const [leiras, setLeiras] = useState('');
  const [prioritas, setPrioritas] = useState<HibajegyPrioritas>('normal');
  const [irodaId, setIrodaId] = useState<string | null>(null);
  const [kepek, setKepek] = useState<File[]>([]);
  const [kepHiba, setKepHiba] = useState<string | null>(null);
  const [urlapHibaSzoveg, setUrlapHiba] = useState<string | null>(null);
  const [folyamatban, setFolyamatban] = useState(false);
  const [hiba, setHiba] = useState<unknown>(null);
  const [eredmeny, setEredmeny] = useState<BekuldesEredmeny | null>(null);
  // Az oldal, ahol a felhasználó a gombot megnyomta (nem az, ahová közben navigált).
  const [oldal, setOldal] = useState('');

  if (aktivId === null) return null;
  const celIroda = irodaId ?? aktivId;
  const irodaNev = irodak.find((i) => i.id === celIroda)?.nev ?? '';

  function megnyit() {
    setOldal(oldalUrl(hely.pathname, window.location.origin));
    setNyitva(true);
  }
  function lezar() {
    // Beküldés közben az Esc is bezárja az ablakot; a beküldés lefut, az eredmény a következő megnyitáskor látszik.
    setNyitva(false);
    // Sikeres beküldés után az űrlap tiszta lappal indul legközelebb; félbehagyott szöveg megmarad.
    if (eredmeny) {
      setEredmeny(null); setCim(''); setLeiras(''); setPrioritas('normal'); setKepek([]); setKepHiba(null); setIrodaId(null);
    }
  }
  function hozzaad(uj: File[]) {
    const r = kepekEllenorzese(kepek.length, uj);
    setKepHiba(r.hibak.length > 0 ? r.hibak.join(' ') : null);
    if (r.elfogadott.length > 0) setKepek((k) => [...k, ...r.elfogadott]);
  }
  function beillesztes(e: ClipboardEvent) {
    const fajlok = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
    if (fajlok.length === 0) return;
    e.preventDefault();
    hozzaad(fajlok);
  }
  async function kuldes(e: FormEvent) {
    e.preventDefault();
    const h = urlapHiba(cim, leiras);
    setUrlapHiba(h);
    if (h) return;
    setHiba(null);
    setFolyamatban(true);
    try {
      const r = await hibajegyBekuldese({ officeId: celIroda, cim, leiras, prioritas, oldalUrl: oldal || null, kepek });
      setEredmeny(r);
      await qc.invalidateQueries({ queryKey: ['hibajegyek'] });
    } catch (err) {
      setHiba(err);
    } finally {
      setFolyamatban(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={megnyit}
        aria-label="Hiba jelzése"
        className="fixed right-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-20 inline-flex size-11 items-center justify-center gap-2 rounded-full border border-vonal-eros bg-hatter text-sm font-medium text-szoveg shadow-[var(--arnyek-2)] transition hover:bg-felulet md:right-6 md:bottom-6 md:size-auto md:min-h-11 md:px-4"
      >
        <span aria-hidden className="text-base leading-none font-semibold text-kiemelt">!</span>
        <span className="hidden md:inline">Hiba jelzése</span>
      </button>

      <Parbeszed nyitva={nyitva} cim={eredmeny ? 'Köszönjük, megkaptuk' : 'Hiba jelzése'} onBezar={lezar}>
        {eredmeny ? (
          <div className="grid gap-4">
            <p role="status">A jelentést rögzítettük, a SocialPro megkapta. Az állapotáról e-mailben értesítünk.</p>
            {eredmeny.kepHiba > 0 && (
              <p role="alert" className="rounded-xl border border-masodlagos px-3.5 py-2.5 text-sm text-masodlagos">
                A jelentés megvan, de {eredmeny.kepHiba} képet nem sikerült feltölteni. A jegy adatlapján válaszban megírhatod, ha a kép fontos.
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-3">
              <Gomb valtozat="masodlagos" onClick={lezar}>Bezárás</Gomb>
              <Link to={`/hibajelentesek/${eredmeny.id}`} onClick={lezar} className="inline-flex min-h-10 items-center justify-center rounded-xl bg-kiemelt px-4 text-sm font-semibold text-kiemelt-szoveg hover:bg-kiemelt-hover">Jelentés megnyitása</Link>
            </div>
          </div>
        ) : (
          <form className="grid gap-4" onSubmit={(e) => void kuldes(e)} onPaste={beillesztes} noValidate>
            <p className="text-sm text-szoveg-halk">Írd le, mit láttál. A képernyőképen lehetnek ügyféladatok (név, telefon): csak azt csatold, ami a hibához kell.</p>
            {jogok.super && irodak.length > 1 ? (
              <Valaszto label="Iroda" value={celIroda} onChange={(e) => setIrodaId(e.target.value)} disabled={folyamatban}>
                {irodak.map((i) => <option key={i.id} value={i.id}>{i.nev}</option>)}
              </Valaszto>
            ) : (
              <p className="text-sm text-szoveg-halk">Iroda: <b className="font-medium text-szoveg">{irodaNev}</b></p>
            )}
            <Mezo label="Cím" value={cim} maxLength={CIM_MAX} onChange={(e) => setCim(e.target.value)} disabled={folyamatban} autoComplete="off" />
            <Mezo label="Mit tapasztaltál?" multiline value={leiras} maxLength={LEIRAS_MAX} onChange={(e) => setLeiras(e.target.value)} disabled={folyamatban}
              suly="Mit csináltál, mit vártál, mi történt helyette?" />
            <Valaszto label="Mennyire sürgős?" value={prioritas} onChange={(e) => setPrioritas(e.target.value as HibajegyPrioritas)} disabled={folyamatban}>
              {PRIORITAS_SORREND.map((p) => <option key={p} value={p}>{PRIORITAS_CIMKE[p]}</option>)}
            </Valaszto>
            <KepValaszto fajlok={kepek} onHozzaad={hozzaad} onTorol={(i) => setKepek((k) => k.filter((_, j) => j !== i))} hiba={kepHiba} tiltva={folyamatban} />
            {oldal && <p className="text-sm break-all text-szoveg-halk">Oldal: {oldal}</p>}
            {urlapHibaSzoveg && <p role="alert" className="text-sm text-hiba">{urlapHibaSzoveg}</p>}
            <HibajegyHiba hiba={hiba} />
            <div className="flex flex-wrap justify-end gap-3">
              <Gomb valtozat="masodlagos" onClick={lezar} disabled={folyamatban}>Mégsem</Gomb>
              <Gomb type="submit" toltes={folyamatban}>Beküldés</Gomb>
            </div>
          </form>
        )}
      </Parbeszed>
    </>
  );
}
